import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants.js';
import { logStage, STAGES } from '../utils/IPLogger.js';

export default class Level2CutsceneScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Level2CutsceneScene' });
    this.currentLine = 0;
    this.currentChar = 0;
    this.currentPart = 0;
    this.textObjects = [];
    this.cursor = null;
    this.isTyping = false;
    this.music = null;
  }

  create() {
    // Log arrival at level 2 cutscene
    logStage(STAGES.LEVEL_2_CUTSCENE);

    // Black background
    this.cameras.main.setBackgroundColor('#000000');

    // Level 2 story - The Power Plant mission briefing
    this.storyParts = [
      // Part 1: The Revelation (Helicopter interior)
      [
        { text: 'EXTRACTION SUCCESSFUL', delay: 40, pause: 1200, color: '#00ff00' },
        { text: '', delay: 0, pause: 400 },
        { text: 'DR. VASQUEZ:', delay: 45, pause: 600, color: '#00ffff' },
        { text: '"SERGEANT... THERE IS SOMETHING', delay: 30, color: '#ffffff' },
        { text: 'YOU NEED TO KNOW."', delay: 30, pause: 1000, color: '#ffffff' },
        { text: '', delay: 0, pause: 400 },
        { text: '"THE ZOMBIES... THEY ARE NOT', delay: 35, color: '#ffffff' },
        { text: 'SPREADING RANDOMLY."', delay: 35, pause: 800, color: '#ffffff' },
        { text: '', delay: 0, pause: 300 },
        { text: '"THEY ARE BEING CREATED."', delay: 50, pause: 1500, color: '#ff0000' },
      ],
      // Part 2: The LAZARUS Chambers
      [
        { text: 'DUKE:', delay: 45, pause: 600, color: '#ffff00' },
        { text: '"CREATED? GENESYS IS STILL RUNNING?"', delay: 30, pause: 1000, color: '#ffffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'DR. VASQUEZ:', delay: 45, pause: 600, color: '#00ffff' },
        { text: '"THE LAZARUS CHAMBERS... ON AUTOPILOT."', delay: 30, pause: 800, color: '#ffffff' },
        { text: '"EVERY CORPSE IN RANGE IS REANIMATED."', delay: 30, pause: 1000, color: '#ffffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'DR. CHEN:', delay: 45, pause: 600, color: '#ff00ff' },
        { text: '"THE SYSTEM REQUIRES ENORMOUS POWER."', delay: 30, pause: 800, color: '#ffffff' },
        { text: '"THE NEW METRO NUCLEAR PLANT..."', delay: 35, pause: 1500, color: '#ff6600' },
      ],
      // Part 3: The Plan
      [
        { text: 'DUKE:', delay: 45, pause: 600, color: '#ffff00' },
        { text: '"SO WE SHUT DOWN THE PLANT..."', delay: 30, pause: 600, color: '#ffffff' },
        { text: '"WE STOP THE ZOMBIE FACTORY."', delay: 35, pause: 1200, color: '#ffffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'DR. WEBB:', delay: 45, pause: 600, color: '#888888' },
        { text: '"IT CANNOT JUST BE TURNED OFF..."', delay: 30, pause: 800, color: '#ffffff' },
        { text: '', delay: 0, pause: 300 },
        { text: 'DUKE:', delay: 45, pause: 600, color: '#ffff00' },
        { text: '"THEN WE DO IT THE OLD-FASHIONED WAY."', delay: 30, pause: 800, color: '#ffffff' },
        { text: '"WE BLOW IT UP."', delay: 55, pause: 2000, color: '#ff0000' },
      ],
      // Part 4: Mission Briefing
      [
        { text: 'GENERAL MORRISON (RADIO):', delay: 30, pause: 600, color: '#00ff00' },
        { text: '"THE NATIONAL GUARD ARMORY IN SECTOR 12"', delay: 25, pause: 600, color: '#ffffff' },
        { text: '"WAS OVERRUN TWO DAYS AGO."', delay: 25, pause: 800, color: '#ffffff' },
        { text: '', delay: 0, pause: 300 },
        { text: '"MILITARY-GRADE C4 SHOULD STILL BE THERE."', delay: 25, pause: 1000, color: '#ffffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'DUKE:', delay: 45, pause: 600, color: '#ffff00' },
        { text: '"A TRIP TO THE ARMORY..."', delay: 30, pause: 600, color: '#ffffff' },
        { text: '"THEN A VISIT TO THE POWER PLANT."', delay: 30, pause: 800, color: '#ffffff' },
        { text: '"JUST ANOTHER NIGHT IN PARADISE."', delay: 35, pause: 2500, color: '#888888' },
      ],
    ];

    // Create blinking cursor
    this.cursor = this.add.text(40, 60, '_', {
      fontFamily: 'Courier New',
      fontSize: '20px',
      fill: '#00ff00',
    });

    // Blink cursor
    this.cursorTween = this.tweens.add({
      targets: this.cursor,
      alpha: { from: 1, to: 0 },
      duration: 300,
      yoyo: true,
      repeat: -1,
    });

    // CRT scanline effect overlay
    this.createScanlines();

    // Start background music (use cutscene music)
    this.music = this.sound.add('cutscene_music', {
      loop: true,
      volume: 0.5,
    });
    this.music.play();

    // Start typing after fade in
    this.cameras.main.fadeIn(500, 0, 0, 0);
    this.time.delayedCall(800, () => {
      this.startTypingPart(0);
    });

    // Skip on Enter or click
    this.input.keyboard.on('keydown-ENTER', () => this.skipCutscene());
    this.input.keyboard.on('keydown-SPACE', () => this.skipCutscene());
    this.input.keyboard.on('keydown-ESC', () => this.skipCutscene());

    // Skip text at bottom
    this.skipText = this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20, 'PRESS ENTER TO SKIP', {
      fontFamily: 'Courier New',
      fontSize: '12px',
      fill: '#444444',
    });
    this.skipText.setOrigin(1, 1);

    // Time display at top
    this.timeText = this.add.text(GAME_WIDTH - 20, 20, '0500 HOURS - 2 HOURS UNTIL DAWN', {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#ff6600',
    });
    this.timeText.setOrigin(1, 0);
    this.timeText.setAlpha(0.7);
  }

  createScanlines() {
    const graphics = this.add.graphics();
    graphics.setDepth(100);

    // Draw horizontal scanlines
    for (let y = 0; y < GAME_HEIGHT; y += 3) {
      graphics.fillStyle(0x000000, 0.15);
      graphics.fillRect(0, y, GAME_WIDTH, 1);
    }
  }

  startTypingPart(partIndex) {
    this.currentPart = partIndex;
    this.currentLine = 0;
    this.currentChar = 0;
    this.isTyping = true;

    // Clear previous text objects
    this.textObjects.forEach(obj => obj.destroy());
    this.textObjects = [];

    // Reset cursor position
    this.cursor.setPosition(40, 60);
    this.cursor.setVisible(true);

    this.typeLine();
  }

  typeLine() {
    // Guard against being called after scene cleanup
    if (!this.isTyping) {
      return;
    }

    const currentStoryPart = this.storyParts[this.currentPart];

    if (this.currentLine >= currentStoryPart.length) {
      // Done with this part - transition to next
      this.transitionToNextPart();
      return;
    }

    const line = currentStoryPart[this.currentLine];
    const yPos = 60 + this.currentLine * 28;

    // Empty line - just pause
    if (line.text === '') {
      this.time.delayedCall(line.pause || 300, () => {
        this.currentLine++;
        this.typeLine();
      });
      return;
    }

    // Create text object for this line
    const textObj = this.add.text(40, yPos, '', {
      fontFamily: 'Courier New',
      fontSize: '20px',
      fill: line.color || '#ffffff',
    });
    this.textObjects.push(textObj);

    // Update cursor color to match text
    this.cursor.setFill(line.color || '#ffffff');

    // Type each character
    this.currentChar = 0;
    this.typeChar(textObj, line);
  }

  typeChar(textObj, line) {
    // Guard against destroyed text objects (e.g., when skipping cutscene)
    if (!textObj || !textObj.active || !this.isTyping) {
      return;
    }

    if (this.currentChar >= line.text.length) {
      // Finished this line
      const pauseTime = line.pause || 400;
      this.time.delayedCall(pauseTime, () => {
        if (this.isTyping) {
          this.currentLine++;
          this.typeLine();
        }
      });
      return;
    }

    // Add next character
    textObj.setText(line.text.substring(0, this.currentChar + 1));

    // Update cursor position
    if (this.cursor && this.cursor.active) {
      this.cursor.setPosition(40 + textObj.width + 2, textObj.y);
    }

    this.currentChar++;

    // Schedule next character
    const charDelay = line.delay || 40;
    this.time.delayedCall(charDelay, () => {
      this.typeChar(textObj, line);
    });
  }

  transitionToNextPart() {
    this.isTyping = false;
    if (this.cursor && this.cursor.active) {
      this.cursor.setVisible(false);
    }

    // Check if there are more parts
    if (this.currentPart < this.storyParts.length - 1) {
      // Fade out current text with static glitch
      this.miniStaticTransition(() => {
        this.startTypingPart(this.currentPart + 1);
      });
    } else {
      // All parts done - finish cutscene
      this.finishCutscene();
    }
  }

  miniStaticTransition(callback) {
    // Quick static burst between parts
    const staticGraphics = this.add.graphics();
    staticGraphics.setDepth(150);

    let frame = 0;
    const maxFrames = 15;

    const staticTimer = this.time.addEvent({
      delay: 40,
      callback: () => {
        staticGraphics.clear();
        frame++;

        // Draw static noise
        const intensity = frame < maxFrames / 2 ? frame / (maxFrames / 2) : (maxFrames - frame) / (maxFrames / 2);
        for (let i = 0; i < intensity * 2000; i++) {
          const x = Phaser.Math.Between(0, GAME_WIDTH);
          const y = Phaser.Math.Between(0, GAME_HEIGHT);
          const brightness = Phaser.Math.Between(50, 200);
          const color = Phaser.Display.Color.GetColor(brightness, brightness, brightness);
          staticGraphics.fillStyle(color, Math.random() * 0.6);
          staticGraphics.fillRect(x, y, 2, 2);
        }

        // Horizontal glitch lines
        if (Math.random() < 0.4) {
          const glitchY = Phaser.Math.Between(0, GAME_HEIGHT);
          staticGraphics.fillStyle(0xffffff, 0.5);
          staticGraphics.fillRect(0, glitchY, GAME_WIDTH, Phaser.Math.Between(1, 4));
        }

        if (frame >= maxFrames) {
          staticTimer.destroy();
          staticGraphics.destroy();
          callback();
        }
      },
      loop: true,
    });

    // Fade out text during transition
    this.tweens.add({
      targets: this.textObjects,
      alpha: 0,
      duration: 300,
    });
  }

  finishCutscene() {
    this.isTyping = false;
    this.cursor.setVisible(false);
    this.skipText.setVisible(false);

    // Show mission title
    const missionTitle = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, 'EPISODE 2', {
      fontFamily: 'Courier New',
      fontSize: '32px',
      fill: '#ff00ff',
    });
    missionTitle.setOrigin(0.5);
    missionTitle.setAlpha(0);

    const missionName = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 10, 'THE POWER PLANT', {
      fontFamily: 'Courier New',
      fontSize: '48px',
      fill: '#00ffff',
    });
    missionName.setOrigin(0.5);
    missionName.setAlpha(0);

    const tagline = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 70, '"When the lights go out... so do the monsters."', {
      fontFamily: 'Courier New',
      fontSize: '16px',
      fill: '#888888',
      fontStyle: 'italic',
    });
    tagline.setOrigin(0.5);
    tagline.setAlpha(0);

    // Fade in mission info
    this.tweens.add({
      targets: [missionTitle, missionName, tagline],
      alpha: 1,
      duration: 1000,
      delay: 500,
    });

    // Wait then transition to game
    this.time.delayedCall(4000, () => {
      this.startStaticEffect();
    });
  }

  startStaticEffect() {
    // Create static noise overlay
    const staticGraphics = this.add.graphics();
    staticGraphics.setDepth(200);

    let staticIntensity = 0;
    const staticTimer = this.time.addEvent({
      delay: 50,
      callback: () => {
        staticGraphics.clear();
        staticIntensity += 0.025;

        // Draw random white noise pixels
        for (let i = 0; i < staticIntensity * 5000; i++) {
          const x = Phaser.Math.Between(0, GAME_WIDTH);
          const y = Phaser.Math.Between(0, GAME_HEIGHT);
          const brightness = Phaser.Math.Between(100, 255);
          const color = Phaser.Display.Color.GetColor(brightness, brightness, brightness);
          staticGraphics.fillStyle(color, Math.random() * 0.8);
          staticGraphics.fillRect(x, y, 2, 2);
        }

        // Add horizontal glitch lines
        if (Math.random() < staticIntensity * 0.4) {
          const glitchY = Phaser.Math.Between(0, GAME_HEIGHT);
          staticGraphics.fillStyle(0xffffff, 0.8);
          staticGraphics.fillRect(0, glitchY, GAME_WIDTH, Phaser.Math.Between(2, 8));
        }

        // When intensity is high enough, transition out
        if (staticIntensity >= 1) {
          staticTimer.destroy();
          this.transitionToGame(staticGraphics);
        }
      },
      loop: true,
    });
  }

  transitionToGame(staticGraphics) {
    // Fade out music
    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 1000,
      });
    }

    // Flash white
    this.cameras.main.flash(300, 255, 255, 255);

    // Fade to black
    this.time.delayedCall(300, () => {
      this.cameras.main.fadeOut(800, 0, 0, 0);
    });

    this.cameras.main.once('camerafadeoutcomplete', () => {
      if (this.music) {
        this.music.stop();
      }
      this.scene.start('Level2Scene');
    });
  }

  skipCutscene() {
    // Clean up timers
    this.time.removeAllEvents();
    this.tweens.killAll();

    // Stop music
    if (this.music) {
      this.music.stop();
    }

    // Destroy text objects
    this.textObjects.forEach(obj => obj.destroy());
    this.textObjects = [];

    // Quick fade to game
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('Level2Scene');
    });
  }

  // Cleanup when scene is shutdown (prevents memory leaks)
  shutdown() {
    // Clean up all timers and tweens
    this.time.removeAllEvents();
    this.tweens.killAll();

    // Stop and clean up music
    if (this.music) {
      this.music.stop();
      this.music = null;
    }

    // Destroy text objects
    if (this.textObjects) {
      this.textObjects.forEach(obj => obj.destroy());
      this.textObjects = [];
    }

    // Destroy cursor
    if (this.cursor) {
      this.cursor.destroy();
      this.cursor = null;
    }
  }
}
